import React, { useState, useMemo } from 'react'
import { Box, Typography, Button, Snackbar, Alert, TextField, MenuItem } from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import ExportButton from '../components/ExportButton'
import AddButton from '../components/AddButton'
import ClearButton from '../components/ClearButton'
import ActionBar from '../components/ActionBar'
import { areasFuncionaisApi } from '../api/areasFuncionais'
import { gruposApi } from '../api/grupos'
import { empresasApi } from '../api/empresas'
import { AreaFuncional, AreaFuncionalCreate, Empresa } from '../types/api'
import AreasFuncionaisTable from '../components/AreasFuncionais/AreasFuncionaisTable'
import AreaFuncionalForm from '../components/AreasFuncionais/AreaFuncionalForm'
import { createLogger } from '../utils/logger'

const logger = createLogger('AreasFuncionaisPage')

const AreasFuncionaisPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [selectedArea, setSelectedArea] = useState<AreaFuncional | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [areaToDelete, setAreaToDelete] = useState<AreaFuncional | null>(null)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })
  const [grupoFiltro, setGrupoFiltro] = useState<number | ''>('')
  const [empresaFiltro, setEmpresaFiltro] = useState<number | ''>('')

  // Buscar grupos para o filtro
  const { data: grupos = [] } = useQuery({
    queryKey: ['grupos'],
    queryFn: () => gruposApi.list({ includeInactive: true }),
  })

  // Buscar empresas para o filtro (eixo "alinhado por empresa": a empresa resolve o grupo dela)
  const { data: empresas = [] } = useQuery<Empresa[]>({
    queryKey: ['empresas', 'areas-funcionais'],
    queryFn: () => empresasApi.list(undefined, { includeInactive: true }),
  })

  const empresasFiltradas = useMemo(
    () => (grupoFiltro ? empresas.filter((empresa) => empresa.grupo_id === Number(grupoFiltro)) : empresas),
    [empresas, grupoFiltro]
  )

  const exportColumns = useMemo(
    () => [
      { key: 'id', label: 'ID' },
      {
        key: 'grupo_id',
        label: 'Grupo',
        format: (value: number) => grupos.find((g) => g.id === value)?.nome || `Grupo ${value}`,
      },
      {
        key: 'empresas_grupo',
        label: 'Empresas do grupo',
        format: (value: number) =>
          empresas
            .filter((empresa) => empresa.grupo_id === value)
            .map((empresa) => empresa.nome)
            .join(', '),
      },
      { key: 'nome', label: 'Nome' },
      { key: 'descricao', label: 'Descrição' },
      { key: 'iduser', label: 'ID User' },
    ],
    [grupos, empresas]
  )

          // Buscar áreas funcionais com filtro
          const {
            data: areas = [],
            isLoading,
            error,
            isError,
          } = useQuery<AreaFuncional[]>({
            queryKey: ['areas-funcionais', grupoFiltro, empresaFiltro],
            queryFn: () =>
              areasFuncionaisApi.list({
                grupo_id: grupoFiltro ? Number(grupoFiltro) : undefined,
                empresa_id: empresaFiltro ? Number(empresaFiltro) : undefined,
              }),
            retry: false, // Não tentar novamente em caso de erro 401/403
          })
  
  // Log para debug
  React.useEffect(() => {
    if (isError) {
      logger.error('Erro detectado no React Query:', error)
    }
    if (areas.length === 0 && !isLoading && !isError) {
      logger.debug('Nenhuma área funcional encontrada (sem erro)')
    }
  }, [isError, error, areas.length, isLoading])

  const createMutation = useMutation({
    mutationFn: (data: AreaFuncionalCreate) => areasFuncionaisApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas-funcionais'] })
      setSnackbar({
        open: true,
        message: 'Área funcional criada com sucesso',
        severity: 'success',
      })
      setFormOpen(false)
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Erro ao criar área funcional',
        severity: 'error',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<AreaFuncionalCreate> }) =>
      areasFuncionaisApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas-funcionais'] })
      setSnackbar({
        open: true,
        message: 'Área funcional atualizada com sucesso',
        severity: 'success',
      })
      setFormOpen(false)
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Erro ao atualizar área funcional',
        severity: 'error',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => areasFuncionaisApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['areas-funcionais'] })
      setSnackbar({
        open: true,
        message: 'Área funcional deletada com sucesso',
        severity: 'success',
      })
      setDeleteConfirmOpen(false)
      setAreaToDelete(null)
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Erro ao deletar área funcional',
        severity: 'error',
      })
    },
  })

  const handleCreate = () => {
    setSelectedArea(null)
    setFormOpen(true)
  }

  const handleEdit = (area: AreaFuncional) => {
    setSelectedArea(area)
    setFormOpen(true)
  }

  const handleDelete = (area: AreaFuncional) => {
    setAreaToDelete(area)
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (areaToDelete) {
      deleteMutation.mutate(areaToDelete.id)
    }
  }

  const handleFormSubmit = async (data: AreaFuncionalCreate) => {
    if (selectedArea) {
      await updateMutation.mutateAsync({ id: selectedArea.id, data })
    } else {
      await createMutation.mutateAsync(data)
    }
  }

  // Verificar se há erro antes de renderizar
  if (isError || error) {
    const errorResponse = (error as any)?.response
    const errorMessage = errorResponse?.data?.detail || 
                        (error as any)?.message || 
                        'Erro ao carregar áreas funcionais'
    const statusCode = errorResponse?.status
    
    logger.error('Erro ao carregar áreas funcionais:', error)
    
    return (
      <Box p={3}>
        <Alert severity="error">
          <Typography variant="body1" fontWeight="bold">
            Erro ao carregar áreas funcionais
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {errorMessage}
          </Typography>
          {statusCode === 401 && (
            <Typography variant="body2" sx={{ mt: 1, color: 'warning.main' }}>
              ⚠️ Verifique se você está logado e tem permissão de Super Administrador
            </Typography>
          )}
          {statusCode === 403 && (
            <Typography variant="body2" sx={{ mt: 1, color: 'warning.main' }}>
              ⚠️ Acesso negado. Apenas Super Administradores podem acessar esta página.
            </Typography>
          )}
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 3 }, width: '100%' }}>
      <ActionBar>
        <ExportButton
          data={Array.isArray(areas) ? areas : []}
          columns={exportColumns}
          filename="areas_funcionais.xlsx"
          sheetName="Áreas Funcionais"
        />
        <AddButton onClick={handleCreate} title="Nova Área Funcional" />
      </ActionBar>

      <Box mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Filtrar por Grupo"
          value={grupoFiltro}
          onChange={(e) => {
            setGrupoFiltro(e.target.value === '' ? '' : Number(e.target.value))
            // Grupo e Empresa filtram o mesmo campo (grupo da entidade): usar um limpa o outro
            setEmpresaFiltro('')
          }}
          sx={{ minWidth: 250 }}
        >
          <MenuItem value="">Todos os grupos</MenuItem>
          {[...grupos]
            .sort((a, b) => a.nome.localeCompare(b.nome, undefined, { sensitivity: 'base' }))
            .map((grupo) => (
              <MenuItem key={grupo.id} value={grupo.id}>
                {grupo.nome}
              </MenuItem>
            ))}
        </TextField>

        <TextField
          select
          label="Filtrar por Empresa"
          value={empresaFiltro}
          onChange={(e) => {
            setEmpresaFiltro(e.target.value === '' ? '' : Number(e.target.value))
            setGrupoFiltro('')
          }}
          sx={{ minWidth: 250 }}
        >
          <MenuItem value="">Todas as empresas</MenuItem>
          {[...empresasFiltradas]
            .sort((a, b) => a.nome.localeCompare(b.nome, undefined, { sensitivity: 'base' }))
            .map((empresa) => (
              <MenuItem key={empresa.id} value={empresa.id}>
                {empresa.nome}
              </MenuItem>
            ))}
        </TextField>

        <ClearButton
          title="Limpar filtros"
          onClick={() => {
            setGrupoFiltro('')
            setEmpresaFiltro('')
          }}
          disabled={grupoFiltro === '' && empresaFiltro === ''}
        />
      </Box>

      <AreasFuncionaisTable
        areas={areas}
        grupos={grupos}
        empresas={empresas}
        onEdit={handleEdit}
        onDelete={handleDelete}
        loading={isLoading}
      />

      <AreaFuncionalForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false)
          setSelectedArea(null)
        }}
        onSubmit={handleFormSubmit}
        area={selectedArea}
        loading={createMutation.isPending || updateMutation.isPending}
      />

      {deleteConfirmOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            p: 3,
            borderRadius: 2,
            boxShadow: 3,
            zIndex: 1300,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Confirmar exclusão
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Tem certeza que deseja deletar a área funcional "{areaToDelete?.nome}"?
          </Typography>
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deletando...' : 'Deletar'}
            </Button>
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AreasFuncionaisPage

